import { createMattermostUserAndAssignTeamUser } from '@goldcast/api/backend';

type AssignmentKey = string;

export type MattermostUserAssignmentResult =
  | { status: 'success' }
  | { status: 'blocked'; statusCode?: number; reason: 'missing-identifiers' | 'terminal-failure' }
  | { status: 'failed'; statusCode?: number; error: unknown };

export type MattermostUserAssignmentParams = {
  userId?: string | null;
  teamId?: string | null;
  eventId?: string | null;
};

const terminalFailureStatusCodes = new Set([403, 404]);
const blockedAssignments = new Map<AssignmentKey, MattermostUserAssignmentResult>();

function getAssignmentKey(userId: string, teamId: string, eventId?: string | null): AssignmentKey {
  return `${userId}:${teamId}:${eventId ?? ''}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNumericProperty(source: Record<string, unknown>, key: string): number | undefined {
  const value = source[key];
  return typeof value === 'number' ? value : undefined;
}

function getStatusCode(error: unknown): number | undefined {
  if (!isRecord(error)) {
    return undefined;
  }

  const directStatusCode = getNumericProperty(error, 'statusCode') ?? getNumericProperty(error, 'status');
  if (directStatusCode) {
    return directStatusCode;
  }

  const response = error.response;
  if (!isRecord(response)) {
    return undefined;
  }

  return getNumericProperty(response, 'status');
}

export function assignMattermostUserToTeam({
  userId,
  teamId,
  eventId
}: MattermostUserAssignmentParams): Promise<MattermostUserAssignmentResult> {
  if (!userId || !teamId) {
    return Promise.resolve({ status: 'blocked', reason: 'missing-identifiers' });
  }

  const assignmentKey = getAssignmentKey(userId, teamId, eventId);
  const blockedResult = blockedAssignments.get(assignmentKey);
  if (blockedResult) {
    return Promise.resolve(blockedResult);
  }

  return createMattermostUserAndAssignTeamUser({
    id: userId,
    body: {
      team_id: teamId
    }
  })
    .then<MattermostUserAssignmentResult>(() => ({ status: 'success' }))
    .catch((error: unknown) => {
      const statusCode = getStatusCode(error);
      if (statusCode && terminalFailureStatusCodes.has(statusCode)) {
        const result: MattermostUserAssignmentResult = {
          status: 'blocked',
          reason: 'terminal-failure',
          statusCode
        };
        blockedAssignments.set(assignmentKey, result);
        return result;
      }

      const result: MattermostUserAssignmentResult = {
        status: 'failed',
        statusCode,
        error
      };
      return result;
    });
}

export function clearMattermostUserAssignmentCacheForTests() {
  blockedAssignments.clear();
}
